// lib/marketing/registry-activity.ts
//
// Baked snapshot of ClinicalTrials.gov's own daily intake: how many NEW
// interventional studies the registry first posted each day, for the 52 full
// weeks ending Sunday 2 August 2026. Drives the /articles hub heatmap
// (components/marketing/articles-activity-heatmap.tsx).
//
// PROVENANCE (honesty rules apply: this is the registry's number, not ours).
// Each value is CTGov API v2 `totalCount` for
//   filter.advanced = AREA[StudyFirstPostDate]RANGE[<day>,<day>]
//                     AND AREA[StudyType]INTERVENTIONAL
// fetched per day on 3 August 2026. It is NOT derived from our change
// archive: pipeline_change_events only has daily precision since 2026-07-14
// (AACT backfill before that is monthly), which is exactly why the founder's
// first choice of metric ("our changes per day, last year") was swapped for
// this one. StudyFirstPostDate is immutable, so these values do not drift;
// re-run the same per-day queries to extend or verify the series.
//
// Shape notes for the heatmap: 52 rows of 7, Monday..Sunday per row, oldest
// week first. Totals at bake time: 36,826 studies; weekend days carry 0.70%
// of the total (CTGov posts on business days), which the chart fades rather
// than hides. Zero days: 119 of 364 (weekends + US federal holidays).

import type { HeatmapColumn } from "@/components/bklit/charts/heatmap"

export const REGISTRY_ACTIVITY_AS_OF = "3 August 2026"

/** Monday the series starts on; week r day c = start + (r*7 + c) days. */
const SERIES_START = { year: 2025, month: 8, day: 4 } as const

/**
 * Level cut points for the heatmap colour scale. Absolute quarter-points of
 * the nonzero range at bake time (min 4, max 382), NOT quantiles: weekday
 * volume is a tight band (IQR 128-180), so quantile buckets paint half the
 * year in the two darkest inks and the grid reads as a black slab. Absolute
 * scaling shows the true shape instead: a steady mid-tone pulse with the
 * genuinely heavy days (holiday catch-ups, fiscal-deadline spikes) standing
 * out dark. Recompute if the series is ever regenerated.
 */
export const REGISTRY_ACTIVITY_LEVEL_THRESHOLDS = [99, 193, 288] as const

/** New interventional studies first posted per day, Mon..Sun per row. */
const WEEKLY_COUNTS: ReadonlyArray<ReadonlyArray<number>> = [
  [4, 173, 154, 142, 170, 0, 0],
  [187, 145, 147, 121, 119, 0, 126],
  [5, 157, 111, 104, 186, 0, 132],
  [6, 110, 122, 142, 112, 0, 0],
  [0, 94, 124, 159, 130, 0, 0],
  [181, 143, 155, 146, 122, 0, 0],
  [152, 168, 125, 128, 139, 0, 0],
  [164, 117, 116, 111, 116, 0, 0],
  [124, 128, 132, 134, 125, 0, 0],
  [111, 113, 121, 103, 43, 0, 0],
  [0, 55, 40, 29, 0, 0, 0],
  [69, 42, 33, 33, 36, 0, 0],
  [34, 32, 32, 35, 35, 0, 0],
  [37, 36, 27, 32, 0, 0, 0],
  [78, 0, 48, 30, 74, 0, 0],
  [165, 166, 153, 165, 162, 0, 0],
  [211, 162, 150, 0, 148, 0, 0],
  [93, 169, 150, 162, 179, 0, 0],
  [140, 153, 136, 121, 152, 0, 0],
  [158, 215, 178, 170, 143, 0, 0],
  [124, 141, 172, 0, 146, 0, 0],
  [150, 155, 102, 0, 132, 0, 0],
  [135, 182, 211, 204, 189, 0, 0],
  [217, 187, 187, 198, 227, 0, 0],
  [0, 246, 192, 222, 230, 0, 0],
  [218, 164, 173, 153, 134, 0, 0],
  [148, 155, 180, 185, 209, 0, 0],
  [201, 188, 169, 198, 194, 0, 0],
  [0, 261, 171, 162, 183, 0, 0],
  [174, 188, 172, 0, 382, 0, 0],
  [175, 204, 142, 150, 201, 0, 0],
  [159, 222, 137, 188, 190, 0, 0],
  [213, 162, 154, 154, 149, 0, 0],
  [141, 149, 120, 0, 291, 0, 0],
  [194, 129, 122, 136, 102, 0, 0],
  [140, 180, 142, 168, 0, 0, 0],
  [332, 179, 124, 156, 156, 0, 0],
  [170, 151, 174, 153, 143, 0, 0],
  [107, 94, 161, 131, 178, 0, 0],
  [178, 140, 193, 147, 157, 0, 0],
  [128, 182, 186, 174, 184, 0, 0],
  [181, 192, 161, 0, 265, 0, 0],
  [0, 138, 149, 131, 200, 0, 0],
  [195, 157, 168, 195, 178, 0, 0],
  [196, 174, 184, 185, 165, 0, 0],
  [142, 183, 194, 169, 0, 0, 0],
  [217, 118, 175, 173, 142, 0, 0],
  [135, 164, 142, 141, 0, 0, 0],
  [213, 181, 132, 173, 153, 0, 0],
  [179, 203, 161, 194, 188, 0, 0],
  [193, 153, 170, 170, 159, 0, 0],
  [167, 174, 173, 164, 147, 0, 0],

]

/**
 * The bklit heatmap's column shape: one column per week, one bin per day.
 * Dates are built with the local-time Date constructor on purpose: the
 * component derives weekday rows and month labels from Date#getDay/#getMonth,
 * and ISO-string parsing (UTC midnight) would shift every cell back a day
 * for viewers west of UTC.
 */
export function registryActivityColumns(): HeatmapColumn[] {
  return WEEKLY_COUNTS.map((week, w) => ({
    bin: w,
    bins: week.map((count, d) => {
      const date = new Date(
        SERIES_START.year,
        SERIES_START.month - 1,
        SERIES_START.day + w * 7 + d,
      )
      return { bin: date.getDay(), count, date }
    }),
  }))
}
